/**
 * ============================================================================
 *  File        : frontend/src/components/StepUpDialog.tsx
 *  Module      : components
 *  Purpose     : 高風險操作 2FA step-up 對話框 + useStepUp hook（spec §9.2）
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  高風險 mutation（P1/P2 手入力、結案、落札確定、重量裁定）送出前,先彈出本
 *  對話框要求一組當下有效的 6 位 TOTP code。確認後把 code 交給呼叫方,呼叫方
 *  以 `stepUpCode` 參數帶進 API（→ `X-2FA-Code` header）。
 *
 *  用法:
 *  ```tsx
 *  const stepUp = useStepUp(t)
 *  // 觸發高風險動作：
 *  <Button onClick={() => stepUp.request((code) => mutation.mutate({ ...vars, code }))}>送出</Button>
 *  // 在 component 樹任一處 render 一次對話框：
 *  {stepUp.dialog}
 *  ```
 *
 *  若使用者尚未啟用 2FA，後端會以 403 `STEP_UP_2FA_REQUIRED` 拒絕；本對話框
 *  仍照常收 code,錯誤訊息由各 mutation 的 onError + toast 呈現（亦提供導向
 *  /admin/security 啟用 2FA 的提示文案 key）。
 */

import { useCallback, useState, type ReactElement, type ReactNode } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'

/** 受 step-up 保護的動作：拿到一組有效 code 後執行（多為呼叫 mutation）。 */
export type StepUpAction = (code: string) => void

interface StepUpDialogProps {
  open: boolean
  t: (key: string) => string
  onConfirm: (code: string) => void
  onCancel: () => void
}

/** 純展示的 step-up 對話框（受控）。data-testid 齊備供 e2e。 */
export function StepUpDialog({ open, t, onConfirm, onCancel }: StepUpDialogProps): ReactElement {
  const [code, setCode] = useState('')

  const handleConfirm = useCallback(() => {
    const trimmed = code.trim()
    if (trimmed === '') return
    setCode('')
    onConfirm(trimmed)
  }, [code, onConfirm])

  const handleCancel = useCallback(() => {
    setCode('')
    onCancel()
  }, [onCancel])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleCancel()
      }}
    >
      <DialogContent data-testid="step-up-dialog">
        <DialogHeader>
          <DialogTitle>{t('stepUp.title')}</DialogTitle>
          <DialogDescription>{t('stepUp.desc')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            handleConfirm()
          }}
        >
          <label className="text-sm font-medium" htmlFor="step-up-code">
            {t('stepUp.codeLabel')}
          </label>
          <Input
            id="step-up-code"
            data-testid="step-up-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            placeholder={t('stepUp.codePlaceholder')}
            aria-label={t('stepUp.codeLabel')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">{t('stepUp.hint')}</p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-testid="step-up-cancel"
              onClick={handleCancel}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" data-testid="step-up-confirm" disabled={code.trim() === ''}>
              {t('stepUp.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** `useStepUp` 回傳值。 */
export interface UseStepUp {
  /** 開啟 step-up 對話框；確認後以拿到的 code 執行 `action`。 */
  request: (action: StepUpAction) => void
  /** 直接 render 的對話框節點（在 component 任一處放一次即可）。 */
  dialog: ReactNode
  /** 對話框是否開啟（少數情境需要）。 */
  open: boolean
}

/**
 * 管理 step-up 對話框生命週期的 hook：保存「待執行動作」與開窗狀態。
 * 確認時把 code 交給待執行動作；取消則清空、不執行。
 */
export function useStepUp(t: (key: string) => string): UseStepUp {
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState<{ action: StepUpAction } | null>(null)

  const request = useCallback((action: StepUpAction) => {
    setPending({ action })
    setOpen(true)
  }, [])

  const onConfirm = useCallback(
    (code: string) => {
      setOpen(false)
      const action = pending?.action
      setPending(null)
      action?.(code)
    },
    [pending],
  )

  const onCancel = useCallback(() => {
    setOpen(false)
    setPending(null)
  }, [])

  const dialog = (
    <StepUpDialog open={open} t={t} onConfirm={onConfirm} onCancel={onCancel} />
  )

  return { request, dialog, open }
}
